import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Calendar } from '@vendasta/meetings';
import { Observable, Subscription } from 'rxjs';
import { retry, take } from 'rxjs/operators';
import { BookMeNowStoreService } from '../book-me-now-store.service';
import { MeetingInfo } from '../bookme-calendar/meeting-info/meeting-info';
import { TimezoneRegionAndZone } from '../bookme/bookme';
import { getTimezone, getZoneFromTimezone } from '../util';

@Component({
  selector: 'app-bookme-cancel',
  templateUrl: './bookme-cancel.component.html',
  styleUrls: ['./bookme-cancel.component.scss'],
  standalone: false,
})
export class BookmeCancelComponent implements OnInit, OnDestroy {
  calendarId!: string; // Use definite assignment assertion
  meetingId!: string;
  authToken!: string;
  cancellationReason = '';
  meetingInfo!: MeetingInfo; // Use definite assignment assertion
  alreadyCanceledMessage = $localize`:@@CANCEL.MEETING_ALREADY_CANCELED:This meeting has already been canceled`;
  error = false;

  calendar$!: Observable<Calendar>; // Use definite assignment assertion
  timezone$!: Observable<string>;
  timezoneOffset$!: Observable<string>;
  meetingLocation$!: Observable<string>;
  displayName: string;

  private subscriptions: Subscription[] = [];

  constructor(
    private readonly route: ActivatedRoute,
    private readonly bookmenowService: BookMeNowStoreService,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.timezone$ = this.bookmenowService.timezone$;
    this.timezoneOffset$ = this.bookmenowService.timezoneOffset$;
    this.meetingId = this.route.snapshot.paramMap.get('meetingId')!;
    this.authToken = this.route.snapshot.queryParams.authToken;
    this.meetingLocation$ = this.bookmenowService.meetingLocation$;

    this.bookmenowService.setMeetingToRescheduleOrCancel(this.meetingId);
    this.bookmenowService
      .getBookedMeetingInfo(this.meetingId, this.authToken)
      .pipe(take(1)) // Take only the first response
      .subscribe((resp) => {
        this.bookmenowService.setTimezone(resp.timeZone.id, false);
        this.bookmenowService.setSelectedTimeSlot({
          start: resp.start,
          end: resp.end,
        });
        this.displayName = resp.displayName;
        this.meetingInfo = {
          startTime: resp.start,
          endTime: resp.end,
          timezone: this.createTimezone(
            resp.start,
            resp.timeZone ? resp.timeZone.id : undefined
          ),
          meetingTypeId: resp.meetingTypeId,
          location: resp.location,
          locationGuideline: resp.locationGuideline,
          locationType: resp.locationType,
          meetingType: resp.meetingType,
          calendarId: resp.calendarId,
          displayName: resp.displayName,
        };
      });

    this.calendar$ = this.bookmenowService.calendar$;
    this.subscriptions.push(
      this.bookmenowService.calendarId$.subscribe(
        (calendarId) => (this.calendarId = calendarId)
      )
    );
  }

  cancelMeeting() {
    this.bookmenowService
      .cancelMeeting({
        meetingId: this.meetingId,
        cancellationReason: this.cancellationReason,
      })
      .pipe(retry(1), take(1))
      .subscribe(
        () => {
          // Navigate to confirmation with only action query param (meetingId is in route)
          const confirmationParams = {
            action: 'cancel',
          };

          this.router.navigate(['confirmation'], {
            relativeTo: this.route,
            queryParams: confirmationParams,
            queryParamsHandling: 'merge',
          });
          this.error = false;
        },
        (err) => {
          if (err.status === 404) {
            this.bookmenowService.setCompletedActionMessage(
              this.alreadyCanceledMessage
            );
            this.error = false;
          } else {
            this.error = true;
          }
        }
      );
  }

  rescheduleMeeting() {
    this.router.navigate(
      [`/reschedules/${this.calendarId}/${this.meetingId}`],
      {
        queryParamsHandling: 'preserve',
      }
    );
  }

  createTimezone(time: Date, timezone?: string): TimezoneRegionAndZone {
    const timezoneRegion = timezone || getTimezone();
    const timezoneOffset = time.getTimezoneOffset();

    return {
      region: timezoneRegion,
      zone: getZoneFromTimezone(timezoneRegion, timezoneOffset),
    };
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
